import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, Alert,
  TextField, Avatar, Divider, IconButton, Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Send as SendIcon,
  SupportAgent as AgentIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import { usePortalAuthStore } from '../../stores/portalAuthStore';
import { useBranding } from '../../utils/BrandingContext';
import api, { extractData } from '../../utils/api';

const statusColors: Record<string, string> = {
  open: '#3b82f6', in_progress: '#f59e0b', resolved: '#10b981', closed: '#6b7280', on_hold: '#8b5cf6',
};
const statusLabels: Record<string, string> = {
  open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed', on_hold: 'On Hold',
};
const priorityColors: Record<string, string> = {
  urgent: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e',
};

interface Reply {
  id: string;
  message: string;
  isStaffReply: boolean;
  senderEmail?: string;
  senderName?: string;
  userEmail?: string;
  userName?: string;
  createdAt: string;
}

interface TicketDetail {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  assignedName?: string;
  assignedEmail?: string;
  createdAt: string;
  updatedAt: string;
  replies: Reply[];
}

const HelpdeskPortalTicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = usePortalAuthStore();
  const { branding } = useBranding();
  const primaryColor = branding.primaryColor || '#6366f1';

  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const repliesEndRef = useRef<HTMLDivElement>(null);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/support/portal/tickets/${id}`);
      const data = extractData(res) as TicketDetail;
      setTicket(data);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTicket(); }, [id]);

  useEffect(() => {
    if (!loading) {
      repliesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticket?.replies, loading]);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    setSendError('');
    try {
      await api.post(`/support/portal/tickets/${id}/replies`, { message: replyText });
      setReplyText('');
      fetchTicket();
    } catch (e: any) {
      setSendError(e?.response?.data?.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const isClosed = ticket?.status === 'closed' || ticket?.status === 'resolved';

  const getInitials = (name?: string, email?: string) => {
    if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    if (email) return email[0].toUpperCase();
    return '?';
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: primaryColor }} />
      </Box>
    );
  }

  if (error || !ticket) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5', p: 3 }}>
        <Alert severity="error">{error || 'Ticket not found'}</Alert>
        <Button startIcon={<BackIcon />} sx={{ mt: 2 }} onClick={() => navigate('/portal/tickets')}>Back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e5e7eb', px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Tooltip title="Back to tickets">
          <IconButton size="small" onClick={() => navigate('/portal/tickets')}>
            <BackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700} fontSize="0.95rem" color="#1f2937" noWrap>
            {ticket.subject}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {ticket.ticketNumber}
          </Typography>
        </Box>
        <Chip
          label={statusLabels[ticket.status] || ticket.status}
          size="small"
          sx={{
            height: 22, fontSize: '0.7rem', fontWeight: 600,
            bgcolor: `${statusColors[ticket.status] || '#6b7280'}22`,
            color: statusColors[ticket.status] || '#6b7280',
          }}
        />
      </Box>

      <Box sx={{ flex: 1, maxWidth: 800, width: '100%', mx: 'auto', px: 2, py: 3 }}>
        {/* Ticket meta */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e5e7eb', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Priority</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: priorityColors[ticket.priority] || '#9ca3af' }} />
                <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>{ticket.priority}</Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Category</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25, textTransform: 'capitalize' }}>{ticket.category}</Typography>
            </Box>
            {ticket.assignedName && (
              <Box>
                <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>{ticket.assignedName}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">Created</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>
                {format(parseISO(ticket.createdAt), 'dd MMM yyyy, HH:mm')}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Original description */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e5e7eb', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: primaryColor, fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>
              {getInitials(user?.firstName, user?.email)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>{user?.firstName || user?.email}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDistanceToNow(parseISO(ticket.createdAt), { addSuffix: true })}
                </Typography>
                <Chip label="Original" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#f3f4f6', color: '#6b7280' }} />
              </Box>
              <Typography variant="body2" color="#374151" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {ticket.description}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Replies */}
        {ticket.replies.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
            {ticket.replies.map(reply => {
              const isStaff = reply.isStaffReply;
              const name = isStaff ? (reply.userName || reply.userEmail || 'Support Team') : (reply.senderName || reply.senderEmail || 'You');
              return (
                <Paper
                  key={reply.id}
                  elevation={0}
                  sx={{
                    p: 2.5, borderRadius: '12px',
                    border: isStaff ? '1px solid #ddd6fe' : '1px solid #e5e7eb',
                    bgcolor: isStaff ? '#faf5ff' : '#fff',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 32, height: 32, flexShrink: 0, fontSize: '0.75rem', fontWeight: 700,
                        bgcolor: isStaff ? '#7c3aed' : primaryColor,
                      }}
                    >
                      {isStaff ? <AgentIcon sx={{ fontSize: 16 }} /> : <PersonIcon sx={{ fontSize: 16 }} />}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600} color={isStaff ? '#6d28d9' : '#1f2937'}>
                          {name}
                        </Typography>
                        {isStaff && (
                          <Chip label="Support" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#ede9fe', color: '#7c3aed' }} />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(parseISO(reply.createdAt), { addSuffix: true })}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="#374151" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {reply.message}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
            <div ref={repliesEndRef} />
          </Box>
        )}

        {/* Reply input */}
        {!isClosed ? (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <Typography variant="body2" fontWeight={600} color="#374151" mb={1.5}>
              Add a Reply
            </Typography>
            {sendError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSendError('')}>{sendError}</Alert>}
            <TextField
              fullWidth multiline rows={4} size="small"
              placeholder="Type your reply here..."
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              sx={{ mb: 1.5 }}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && replyText.trim()) {
                  handleSendReply();
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={sending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SendIcon />}
                onClick={handleSendReply}
                disabled={sending || !replyText.trim()}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', bgcolor: primaryColor }}
              >
                {sending ? 'Sending...' : 'Send Reply'}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Tip: Ctrl+Enter to send quickly
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px dashed #d1d5db', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              This ticket is {statusLabels[ticket.status]?.toLowerCase()}. Sending a new reply will reopen it.
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default HelpdeskPortalTicketDetail;
